import React, { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  handleBranchData,
  handleBranchFetched,
  handleGlobalLoader,
  handleInitialAPICallSuccess,
  handleTableStatusFetched,
  handleTenantData,
  handleTenantFetched,
} from 'store/auth/authSlice';
import {
  handleCustomerDetails,
  handleOrderDetails,
} from 'store/tableStatusAndOrder/tableStatusAndOrderSlice';
import { useAppDispatch, useAppSelector } from 'store/customHooks';
import {
  useGetBranchByIdQuery,
  useGetTableStatusByBranchIdAndTableIdQuery,
  useGetTenantByIdQuery,
} from 'store/initialAPI/initialAPISlice';

interface Props {
  children?: ReactNode;
}

const InitialDataFetcher: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const brandId = queryParams.get('brandId');
  const branchId = queryParams.get('branchId');
  const tableId = queryParams.get('tableId');

  const parsedIds = JSON.parse(sessionStorage.getItem('Ids') || '{}');

  useEffect(() => {
    if (branchId && brandId && tableId) {
      const deviceId = localStorage.getItem('deviceId');
      localStorage.clear();
      sessionStorage.clear();
      if (deviceId) {
        localStorage.setItem('deviceId', deviceId);
      }
      sessionStorage.setItem(
        'Ids',
        JSON.stringify({
          branchId: Number(branchId),
          brandId: Number(brandId),
          tableId: Number(tableId),
        }),
      );
    } else {
      dispatch(handleGlobalLoader(false));
    }
  }, [brandId, branchId, tableId, dispatch]);

  const tenantFetched = useAppSelector((state) => state.auth.tenantFetched);
  const branchFetched = useAppSelector((state) => state.auth.branchFetched);
  const tableStatusFetched = useAppSelector((state) => state.auth.tableStatusFetched);

  // Step 1: Get tenant data
  const {
    data: tenantData,
    isLoading: tenantDataIsLoading,
    isError: tenantDataIsError,
    isSuccess: tenantDataIsSuccess,
    isFetching: tenantDataIsFetching,
  } = useGetTenantByIdQuery(
    {
      brandId: Number(brandId ?? parsedIds?.brandId),
      // branchId: Number(branchId ?? parsedIds?.branchId),
    },
    {
      skip:
        tenantFetched ||
        ['/payment-confirmation', '/payment-status'].includes(location.pathname) ||
        !brandId,
    },
  );

  useEffect(() => {
    if (tenantDataIsSuccess && tenantData) {
      sessionStorage.setItem(
        'CustomerApp-Tenant',
        JSON.stringify({
          tenantName: tenantData.tenantName,
          id: tenantData.id,
          databaseName: tenantData.databaseName,
          shortName: tenantData?.shortName,
        }),
      );
      dispatch(
        handleTenantData({
          tenantName: tenantData.tenantName,
          id: tenantData.id,
          databaseName: tenantData.databaseName,
          shortName: tenantData?.shortName,
          helperData: tenantData,
        }),
      );
      dispatch(handleTenantFetched(true));
    }
  }, [tenantData, tenantDataIsSuccess, dispatch]);

  useEffect(() => {
    const tenant = JSON.parse(sessionStorage.getItem('CustomerApp-Tenant') || '{}');

    if (tenant.id) {
      dispatch(handleTenantFetched(true));
    }
  }, [dispatch]);

  // Step 2: Get branch data after tenant data is fetched
  const {
    data: branchData,
    isLoading: branchIsLoading,
    isError: branchIsError,
    isSuccess: branchIsSuccess,
    isFetching: branchIsFetching,
  } = useGetBranchByIdQuery(
    {
      branchId: Number(branchId ?? parsedIds?.branchId),
    },
    {
      skip:
        !tenantFetched ||
        branchFetched ||
        ['/payment-confirmation', '/payment-status'].includes(location.pathname),
    },
  );

  useEffect(() => {
    if (branchIsSuccess && branchData) {
      sessionStorage.setItem(
        'Branch',
        JSON.stringify({
          branchId: branchData?.branchId,
          id: branchData?.id,
          branchName: branchData?.branchName,
          shortName: branchData?.shortName,
          displayName: branchData?.displayName,
        }),
      );

      dispatch(handleBranchData(branchData));
      dispatch(handleBranchFetched(true));
    }
  }, [branchData, branchIsSuccess, dispatch]);

  useEffect(() => {
    const branch = JSON.parse(sessionStorage.getItem('Branch') || '{}');

    if (branch.shortName) {
      dispatch(handleBranchFetched(true));
    }
  }, [dispatch]);

  // Step 3: Get table status after tenant data is fetched
  const {
    data: tableStatusData,
    isLoading: tableStatusIsLoading,
    isError: tableStatusIsError,
    isSuccess: tableStatusIsSuccess,
    isFetching: tableStatusIsFetching,
  } = useGetTableStatusByBranchIdAndTableIdQuery(
    {
      brandId: Number(brandId ?? parsedIds?.brandId),
      branchId: Number(branchId ?? parsedIds?.branchId),
      tableId: Number(tableId ?? parsedIds?.tableId),
    },
    {
      skip:
        !tenantFetched || ['/payment-confirmation', '/payment-status'].includes(location.pathname),
    },
  );

  useEffect(() => {
    const parsedIds = JSON.parse(sessionStorage.getItem('Ids') || '{}');
    const effectiveBrandId = Number(brandId ?? parsedIds?.brandId);
    const effectiveBranchId = Number(branchId ?? parsedIds?.branchId);
    const effectiveTableId = Number(tableId ?? parsedIds?.tableId);

    if (
      tableStatusIsSuccess &&
      tableStatusData &&
      effectiveBranchId &&
      effectiveBrandId &&
      effectiveTableId
    ) {
      sessionStorage.setItem(
        'Ids',
        JSON.stringify({
          branchId: Number(effectiveBranchId),
          brandId: Number(effectiveBrandId),
          tableId: Number(tableStatusData?.tableId || effectiveTableId),
        }),
      );
      const lsCheckIn = sessionStorage.getItem('CheckIn');
      if (!lsCheckIn) {
        if (
          tableStatusData &&
          Object.hasOwn(tableStatusData, 'isCustomerAssociateNotAssigned') &&
          tableStatusData?.isCustomerAssociateNotAssigned
        ) {
          sessionStorage.setItem('CheckIn', 'CUST_ASSOC_NOT_ASSIGNED');
        } else if (
          tableStatusData &&
          Object.hasOwn(tableStatusData, 'isOrderCheckOut') &&
          tableStatusData?.isOrderCheckOut
        ) {
          sessionStorage.setItem('CheckIn', 'CHECKOUT');
        } else if (
          tableStatusData &&
          Object.hasOwn(tableStatusData, 'isOpenOrder') &&
          tableStatusData?.isOpenOrder
        ) {
          sessionStorage.setItem('CheckIn', 'JOIN');
        } else if (
          tableStatusData &&
          Object.hasOwn(tableStatusData, 'isOpenOrder') &&
          !tableStatusData?.isOpenOrder
        ) {
          sessionStorage.setItem('CheckIn', 'NEW');
        }
        window.dispatchEvent(new Event('storage'));
      }

      // Also populate customer details with table code for the header
      const existingCustomerDetailsStr = sessionStorage.getItem('CustomerDetails');
      const existingCustomerDetails = existingCustomerDetailsStr
        ? JSON.parse(existingCustomerDetailsStr)
        : {};

      if (tableStatusData?.tableCode) {
        const updatedCustomerDetails = {
          ...existingCustomerDetails,
          tableCode: tableStatusData.tableCode,
        };
        sessionStorage.setItem('CustomerDetails', JSON.stringify(updatedCustomerDetails));

        dispatch(handleCustomerDetails(updatedCustomerDetails));
      }

      // Dispatch order details early so header table info is available even before OrderContext fetches it
      if (tableStatusData?.order) {
        dispatch(handleOrderDetails(tableStatusData.order));
      } else if (tableStatusData?.tableName) {
        // Fallback for when there's no active order but we know the table name
        dispatch(
          handleOrderDetails({
            code: tableStatusData.tableCode || '',
            tables: [{ id: tableStatusData.tableId }],
            tableNames: tableStatusData.tableName ? [tableStatusData.tableName] : [],
          }),
        );
      }

      dispatch(handleTableStatusFetched(true));
    }
  }, [tableStatusIsSuccess, tableStatusData, branchId, brandId, tableId, dispatch]);

  // Handle loading states
  useEffect(() => {
    const isFetching = tenantDataIsFetching || tableStatusIsFetching || branchIsFetching;
    const isLoading = tableStatusIsLoading || tenantDataIsLoading || branchIsLoading;

    dispatch(handleGlobalLoader(isFetching));
    dispatch(handleGlobalLoader(isLoading));

    if (tenantDataIsFetching) {
      console.log('Tenant - Fetching....');
    }
    if (branchIsFetching) {
      console.log('Branch - Fetching....');
    }
    if (tableStatusIsFetching) {
      console.log('Table Status - Fetching....');
    }
    if (branchIsLoading) {
      console.info('Branch - Loading....');
    }
    if (tableStatusIsLoading) {
      console.info('Table Status - Loading....');
    }
    if (tenantDataIsLoading) {
      console.info('Tenant Status - Loading....');
    }
  }, [
    tableStatusIsLoading,
    tenantDataIsLoading,
    tenantDataIsFetching,
    tableStatusIsFetching,
    dispatch,
    branchIsFetching,
    branchIsLoading,
  ]);

  // Handle error states
  useEffect(() => {
    if (tableStatusIsError) {
      console.error('Table Status - Error during fetch. Please try again.');
    }
    if (tenantDataIsError) {
      console.error('Tenant - Error during fetch. Please try again.');
    }
    if (branchIsError) {
      console.error('Branch - Error during fetch. Please try again.');
    }
  }, [tableStatusIsError, tenantDataIsError, branchIsError]);

  // Set success when all data is fetched
  useEffect(() => {
    const tenant = JSON.parse(sessionStorage.getItem('CustomerApp-Tenant') || '{}');
    const branch = JSON.parse(sessionStorage.getItem('Branch') || '{}');
    const allSuccess = tableStatusFetched && !tableStatusIsError;

    if (allSuccess && tenant?.id && branch?.shortName) {
      console.log('All 3 API Success');
      dispatch(handleInitialAPICallSuccess(true));
    }
  }, [dispatch, tableStatusFetched, tableStatusIsError, tenantFetched, branchFetched]);

  return <div>{children}</div>;
};

export default InitialDataFetcher;
